import { useState } from 'react';
import { usePartnerAccountants } from '../../hooks/partner-accountants/usePartnerAccountants.ts';
import { usePartnerStaffs } from '../../hooks/partner-staffs/usePartnerStaffs.ts';
import { Button, Title, Stack, Group, Paper, Tabs, Menu } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import PartnerAccountantTable from '../table/PartnerAccountantTable.tsx';
import useCreatePartnerAccountant from '../../hooks/partner-accountants/useCreatePartnerAccountant.ts';
import PartnerStaffTable from '../table/PartnerStaffTable.tsx';
import useCreatePartnerStaff from '../../hooks/partner-staffs/useCreatePartnerStaff.ts';
import { notifySuccess } from '../../utils/NotifySuccess.tsx';
import CreatePartnerAccountantModal from '../modal/partner-accountants/CreatePartnerAccountantModal.tsx';
import CreatePartnerStaffModal from '../modal/partner-staffs/CreatePartnerStaffModal.tsx';

type PeopleManagementTab = 'accountants' | 'staffs';

export default function PeopleManagementCard({
  partnerId,
}: {
  partnerId: string;
}) {
  const [peopleManagementTab, setPeopleManagementTab] =
    useState<PeopleManagementTab>('accountants');

  const { data: accountants = [], isLoading: partnerAccountantsIsLoading } =
    usePartnerAccountants(partnerId);
  const { data: staffs = [], isLoading: partnerStaffsIsLoading } =
    usePartnerStaffs(partnerId);

  const {
    mutate: createPartnerAccountant,
    isPending: createPartnerAccountantIsPending,
  } = useCreatePartnerAccountant();
  const { mutate: createPartnerStaff, isPending: createPartnerStaffIsPending } =
    useCreatePartnerStaff();
  const queryClient = useQueryClient();

  const [
    accountantModalOpened,
    { open: openAccountantModal, close: closeAccountantModal },
  ] = useDisclosure(false);
  const [staffModalOpened, { open: openStaffModal, close: closeStaffModal }] =
    useDisclosure(false);

  function handleConfirmCreatePartnerAccountant({ formValues }) {
    if (createPartnerAccountantIsPending) return;

    createPartnerAccountant(
      { partnerId, ...formValues },
      {
        onSuccess: () => {
          closeAccountantModal();
          notifySuccess('新增會計人員成功');
          queryClient.invalidateQueries({
            queryKey: ['partnerAccountants', partnerId],
          });
        },
      },
    );
  }

  function handleConfirmCreatePartnerStaff({ formValues }) {
    if (createPartnerStaffIsPending) return;

    createPartnerStaff(
      { partnerId, ...formValues },
      {
        onSuccess: () => {
          closeStaffModal();
          notifySuccess('新增廠商後台管理員成功');
          queryClient.invalidateQueries({
            queryKey: ['partnerStaffs', partnerId],
          });
        },
      },
    );
  }

  return (
    <>
      {accountantModalOpened && (
        <CreatePartnerAccountantModal
          opened={accountantModalOpened}
          onClose={closeAccountantModal}
          onConfirm={handleConfirmCreatePartnerAccountant}
          isPending={createPartnerAccountantIsPending}
        />
      )}
      {staffModalOpened && (
        <CreatePartnerStaffModal
          opened={staffModalOpened}
          onClose={closeStaffModal}
          onConfirm={handleConfirmCreatePartnerStaff}
          isPending={createPartnerStaffIsPending}
        />
      )}

      <Paper radius="lg" shadow="xs">
        <Group justify="space-between" p="xl">
          <Title order={3}>人員管理</Title>
          <Menu shadow="md" width={160}>
            <Menu.Target>
              <Button leftSection={<IconPlus size={20} />}>新增人員</Button>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item onClick={openAccountantModal}>會計人員</Menu.Item>
              <Menu.Item onClick={openStaffModal}>廠商後台管理員</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
        <Stack px="md" pb="xl">
          <Tabs defaultValue="accountants" mb="sm">
            <Tabs.List>
              <Tabs.Tab
                value="accountants"
                onClick={() => setPeopleManagementTab('accountants')}
              >
                {`會計人員 (${accountants.length})`}
              </Tabs.Tab>
              <Tabs.Tab
                value="staffs"
                onClick={() => setPeopleManagementTab('staffs')}
              >
                {`廠商後台管理員 (${staffs.length})`}
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>
          <PartnerAccountantTable
            partnerId={partnerId}
            accountants={accountants}
            isLoading={partnerAccountantsIsLoading}
            hidden={peopleManagementTab != 'accountants'}
          />
          <PartnerStaffTable
            partnerId={partnerId}
            staffs={staffs}
            isLoading={partnerStaffsIsLoading}
            hidden={peopleManagementTab != 'staffs'}
          />
        </Stack>
      </Paper>
    </>
  );
}
